/**
 * StatusBadge — Phase 6a.19 per Spec §21.10.4.
 *
 * Visual surface for `EngagementStatus`. Wraps the shadcn Badge with
 * status-specific color classes (emerald for calculated, amber for
 * stale, muted for draft) and an i18n label sourced from
 * `history.status.<status>`. Used on every history card; reusable
 * anywhere an engagement status needs to be communicated.
 */

import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { EngagementStatus } from "@/types";

export interface StatusBadgeProps {
  status: EngagementStatus;
  className?: string;
}

const STATUS_CLASSES: Record<EngagementStatus, string> = {
  draft: "bg-muted text-muted-foreground border-border",
  calculated:
    "bg-emerald-100 dark:bg-emerald-950/30 text-emerald-800 dark:text-emerald-200 border-emerald-300 dark:border-emerald-700",
  stale:
    "bg-amber-100 dark:bg-amber-950/30 text-amber-800 dark:text-amber-200 border-amber-300 dark:border-amber-700",
};

export function StatusBadge({ status, className }: StatusBadgeProps) {
  const { t } = useTranslation();
  return (
    <Badge
      variant="outline"
      className={cn(STATUS_CLASSES[status], className)}
      data-testid={`status-badge-${status}`}
    >
      {t(`history.status.${status}` as never) as string}
    </Badge>
  );
}
